import { NIGHTMARES, SIMA, SIMA_BOSS, STRATUM, WARDEN, type EnemyKind, type NightmareType } from '@somnhold/content';
import { hexRing, hexToWorld, type Hex } from '../hex.ts';
import { ANGLE_STEPS, cosA, dist2, iatan2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, DungeonRoom, DungeonState, Enemy, Hero, SimaRun } from '../types.ts';
import { damageHero, ev, findHero, spawnEnemy } from './combat.ts';
import { captainOf, chill, onSegment, simaDmgBp, simaHpBp } from './sima.ts';

/*
 * The Chasm's halls (docs/SIMA.md, «Guardianes»): a captain on most floors, a guardian every fifth. Each new guardian
 * has one rule that reads in three seconds; its move under way is `sima.act`, which the screen draws and names.
 */

type Spawn = (kind: EnemyKind, tier: 1 | 2 | 3, elite: boolean, x: number, y: number, hp?: number) => Enemy;
const standing = (h: Hero): boolean => h.downTicks === 0;
const W = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);
const OLD = new Set<EnemyKind>(['warden', 'magmaLord', 'colossus']);
export const SIMA_GUARDIAN_KINDS = new Set<EnemyKind>(['leechMother', 'rootking', 'sporeMother', 'frostLady', 'prism', 'archivist', 'bellows', 'broodQueen', 'marrowWorm']);
/** Struck like nightmares but never moving or striking. */
export const inertKind = (k: EnemyKind): boolean => k === 'crystal' || k === 'cocoon' || k === 'iceBlock';

/** Heroes by party size for a guardian's numbers (more heroes, more ice to break, a longer reading). */
const partyBp = (s: BattleState): number => 10000 + imax(0, s.heroes.length - 1) * 5000;

/** The hall wakes: its captain (two with the pair sign), or the stratum's guardian with what its rule needs on the floor. */
export const simaHall = (s: BattleState, d: DungeonState, room: DungeonRoom, spawn: Spawn): void => {
  const sm = d.sima as SimaRun;
  const c = W(room);
  if (!sm.threshold) {
    const kind = captainOf(sm, sm.stratum);
    const two = sm.signs.includes('pair');
    for (let k = 0; k < (two ? 2 : 1); k++) {
      const e = spawn(kind, 2, true, c.x + (two ? (k * 2 - 1) * 900 : 0), c.y);
      e.hp = e.maxHp = mulBp(mulBp(e.maxHp, SIMA.captainHpBp), two ? 7000 : 10000);
      e.damage = mulBp(e.damage, SIMA.captainDmgBp);
      if (k === 0) sm.boss = e.id;
    }
    d.boss = sm.boss;
    ev(s, { t: 'skill', hero: sm.boss, skill: 'simaCaptain', x: c.x, y: c.y });
    return;
  }
  const kind = STRATUM[sm.stratum].guardian;
  const bp = OLD.has(kind) ? SIMA.oldGuardianHpBp[kind as 'warden'] : 14000;
  const hp = mulBp(mulBp(mulBp(sm.guardianHp, SIMA.baseLevelBp), simaHpBp(sm.floor)), bp);
  const e = spawn(kind, 1, false, c.x, c.y, hp);
  e.damage = mulBp(e.damage, SIMA.guardianDmgBp);
  if (kind === 'warden') {
    const t = WARDEN.meleeByParty;
    e.damage = mulBp(e.damage, t[imin(s.heroes.length, t.length - 1)] as number);
    e.phase = 1;
    e.timer = WARDEN.every[0] as number;
  }
  sm.boss = e.id;
  d.boss = e.id;
  e.timer = 0;
  // What the rule needs on the floor.
  if (kind === 'leechMother')
    sm.props.push({
      kind: 'pool',
      q: room.q,
      r: room.r,
      x: c.x,
      y: c.y,
      r2: 2200,
    });
  if (kind === 'bellows') {
    const ring = hexRing(3);
    const off = rngInt(s.rng, ring.length);
    const n = SIMA_BOSS.bellows.anvils + (s.heroes.length > 4 ? 1 : 0);
    for (let k = 0; k < n; k++) {
      const h = ring[(off + idiv(k * ring.length, n)) % ring.length] as Hex;
      const p = W({ q: room.q + h.q, r: room.r + h.r });
      sm.props.push({
        kind: 'anvil',
        q: room.q + h.q,
        r: room.r + h.r,
        x: p.x,
        y: p.y,
        r2: 650,
      });
    }
  }
  if (kind === 'prism') {
    const ring = hexRing(3);
    const off = rngInt(s.rng, ring.length);
    const n = SIMA_BOSS.prism.crystals + (s.heroes.length > 4 ? 1 : 0);
    for (let k = 0; k < n; k++) {
      const h = ring[(off + idiv(k * ring.length, n)) % ring.length] as Hex;
      const p = W({ q: room.q + h.q, r: room.r + h.r });
      const cr = spawnEnemy(s, 'crystal', 1, false, 0, p.x, p.y, mulBp(mulBp(SIMA_BOSS.prism.crystalHp, partyBp(s)), simaHpBp(sm.floor)));
      cr.owner = e.id;
    }
  }
  ev(s, { t: 'skill', hero: e.id, skill: 'simaGuardian', x: c.x, y: c.y });
};

/** The nearest standing hero to a point (lowest id on a tie). */
const nearest = (s: BattleState, x: number, y: number): Hero | null => {
  let best: Hero | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    const dd = dist2(x, y, h.x, h.y);
    if (dd < bd) {
      bd = dd;
      best = h;
    }
  }
  return best;
};

const act = (sm: SimaRun, e: Enemy, kind: NonNullable<SimaRun['act']>['kind'], from: number, until: number, o: Partial<NonNullable<SimaRun['act']>> = {}): void => {
  sm.act = {
    kind,
    by: e.id,
    from,
    until,
    x: e.x,
    y: e.y,
    tx: e.x,
    ty: e.y,
    r: 0,
    ...o,
  };
};

/** A strike marked on the floor that lands `warn` later (the Chasm's own zones: roots, a quake). */
const quake = (s: BattleState, x: number, y: number, r: number, warn: number, dmg: number, hold: number): void => {
  s.zones.push({
    id: s.nextId++,
    kind: 'sima',
    haz: 'quake',
    x,
    y,
    radius: r,
    cells: [],
    ticks: warn + 8,
    every: 0,
    damage: dmg,
    slowBp: 0,
    pid: '',
    warn,
    stun: hold,
  });
};

/**
 * One tick of a Chasm guardian's rule. True when it does nothing else this tick (no walking, no blows): drained
 * of its pool, swollen, reading, drawing breath, under the ground.
 */
export const simaBossTick = (s: BattleState, e: Enemy): boolean => {
  const d = s.dungeon;
  const sm = d?.sima;
  if (!d || !sm || e.id !== sm.boss || !SIMA_GUARDIAN_KINDS.has(e.kind)) return false;
  const t = s.tick;
  const a = sm.act && sm.act.by === e.id ? sm.act : null;
  if (a && t >= a.until) {
    end(s, sm, e, a);
    sm.act = null;
    return false;
  }
  const age = t - e.born;
  const dmg = (n: number): number => mulBp(mulBp(mulBp(n, sm.dmgBp), simaDmgBp(sm.floor)), SIMA.guardianDmgBp);
  switch (e.kind) {
    case 'leechMother': {
      const B = SIMA_BOSS.leechMother;
      const pool = sm.props.find((p) => p.kind === 'pool');
      // She never leaves her pool.
      if (pool && !withinRange(pool.x, pool.y, e.x, e.y, pool.r2 - 400)) {
        e.x = pool.x + idiv((e.x - pool.x) * (pool.r2 - 400), imax(1, isqrt(dist2(e.x, e.y, pool.x, pool.y))));
        e.y = pool.y + idiv((e.y - pool.y) * (pool.r2 - 400), imax(1, isqrt(dist2(e.x, e.y, pool.x, pool.y))));
      }
      if (!a && age > 0 && age % B.every === B.every - 1) {
        act(sm, e, 'low', t, t + B.low, { r: pool?.r2 ?? 2200 });
        ev(s, { t: 'skill', hero: e.id, skill: 'simaLow', x: e.x, y: e.y });
        return true;
      }
      if (a?.kind === 'low') {
        e.anim = 'stun';
        return true;
      }
      // Covered: she mends.
      if (t % 12 === 0 && e.hp < e.maxHp) e.hp = imin(e.maxHp, e.hp + mulBp(e.maxHp, B.mendBpPerSec));
      return false;
    }
    case 'rootking': {
      const B = SIMA_BOSS.rootking;
      if (!a && age > 0 && age % B.every === B.every - 1) {
        act(sm, e, 'roots', t, t + B.warn, { r: B.radius });
        quake(s, e.x, e.y, B.radius, B.warn, dmg(B.squeeze), B.holdTicks);
        e.anim = 'cast';
        e.animT = 0;
        ev(s, { t: 'skill', hero: e.id, skill: 'simaRoots', x: e.x, y: e.y });
        return true;
      }
      return a?.kind === 'roots';
    }
    case 'sporeMother': {
      const B = SIMA_BOSS.sporeMother;
      if (!a && age > 0 && age % B.every === B.every - 1) {
        act(sm, e, 'swell', t, t + B.swell, { r: B.radius });
        ev(s, { t: 'skill', hero: e.id, skill: 'simaSwell', x: e.x, y: e.y });
        return true;
      }
      return a?.kind === 'swell';
    }
    case 'frostLady': {
      const B = SIMA_BOSS.frostLady;
      if (!a && age > 0 && age % B.every === B.every - 1) {
        // One hero in ice, two in a party past four.
        const up = s.heroes.filter(standing);
        const n = up.length > 4 ? 2 : 1;
        const first = rngInt(s.rng, imax(1, up.length));
        for (let k = 0; k < n && k < up.length; k++) {
          const h = up[(first + k * imax(1, idiv(up.length, 2))) % up.length] as Hero;
          const ice = spawnEnemy(s, 'iceBlock', 1, false, 0, h.x, h.y, mulBp(mulBp(B.iceHp, partyBp(s)), simaHpBp(sm.floor)));
          ice.encases = h.id;
          ice.owner = e.id;
          // Alone, nobody can break it: it gives way by itself sooner.
          ice.timer = up.length > 1 ? B.maxTicks : B.alone;
          h.stun = imax(h.stun, ice.timer);
          h.moving = false;
          if (k === 0) act(sm, e, 'ice', t, t + 18, { target: h.id, tx: h.x, ty: h.y });
          ev(s, { t: 'skill', hero: e.id, skill: 'simaIce', x: h.x, y: h.y });
        }
        e.anim = 'cast';
        e.animT = 0;
      }
      // Her breath: a cone of frost toward the nearest hero.
      if (age > 0 && age % B.breath.every === B.breath.every - 1) cone(s, e, B.breath.range, B.breath.arc, dmg(B.breath.dmg), 'frost');
      return false;
    }
    case 'prism': {
      const B = SIMA_BOSS.prism;
      if (age > 0 && age % B.every === B.every - 1) {
        // One sweeping beam, and one more for every crystal still standing.
        const crystals = s.enemies.filter((o) => o.kind === 'crystal' && o.hp > 0 && o.owner === e.id).length;
        const up = s.heroes.filter(standing);
        for (let k = 0; k <= crystals && up.length; k++) {
          const h = up[(k + rngInt(s.rng, up.length)) % up.length] as Hero;
          const dir = iatan2(h.y - e.y, h.x - e.x);
          const len = 9000;
          s.zones.push({
            id: s.nextId++,
            kind: 'sima',
            haz: 'beam',
            x: e.x,
            y: e.y,
            tx: e.x + idiv(cosA(dir) * len, 10000),
            ty: e.y + idiv(sinA(dir) * len, 10000),
            radius: B.width,
            cells: [],
            ticks: B.warn + 8,
            every: 0,
            damage: dmg(B.damage),
            slowBp: 0,
            pid: '',
            warn: B.warn,
          });
        }
        e.anim = 'cast';
        e.animT = 0;
        ev(s, { t: 'skill', hero: e.id, skill: 'simaBeams', x: e.x, y: e.y });
      }
      return false;
    }
    case 'archivist': {
      const B = SIMA_BOSS.archivist;
      if (!a && age > 0 && age % B.every === B.every - 1) {
        const bar = mulBp(mulBp(B.bar, partyBp(s)), simaHpBp(imin(sm.floor, 40)));
        act(sm, e, 'read', t, t + B.read, { bar, barMax: bar });
        ev(s, { t: 'skill', hero: e.id, skill: 'simaRead', x: e.x, y: e.y });
        return true;
      }
      if (a?.kind === 'read') {
        e.anim = 'cast';
        return true;
      }
      return false;
    }
    case 'bellows': {
      const B = SIMA_BOSS.bellows;
      if (!a && age > 0 && age % B.every === B.every - 1) {
        const h = nearest(s, e.x, e.y);
        if (h) {
          act(sm, e, 'inhale', t, t + B.inhale, {
            tx: h.x,
            ty: h.y,
            r: B.range,
          });
          ev(s, {
            t: 'skill',
            hero: e.id,
            skill: 'simaInhale',
            x: e.x,
            y: e.y,
          });
          return true;
        }
      }
      if (a?.kind === 'inhale') {
        // It draws the heroes in.
        for (const h of s.heroes) {
          if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, B.range)) continue;
          const dd = imax(1, isqrt(dist2(e.x, e.y, h.x, h.y)));
          if (dd < 1400) continue;
          h.x -= idiv((h.x - e.x) * B.pull, dd);
          h.y -= idiv((h.y - e.y) * B.pull, dd);
        }
        e.anim = 'cast';
        return true;
      }
      return a?.kind === 'blow';
    }
    case 'broodQueen': {
      const B = SIMA_BOSS.broodQueen;
      if (age > 0 && age % B.every === B.every - 1) {
        const room = s.map.dungeon?.rooms[(s.map.dungeon?.rooms.length ?? 1) - 1];
        if (room) {
          const ring = hexRing(2 + rngInt(s.rng, 2));
          const h = ring[rngInt(s.rng, ring.length)] as Hex;
          const p = W({ q: room.q + h.q, r: room.r + h.r });
          const c = spawnEnemy(s, 'cocoon', 1, false, 0, p.x, p.y, mulBp(mulBp(B.cocoonHp, partyBp(s)), simaHpBp(sm.floor)));
          c.owner = e.id;
          c.timer = B.hatch;
          e.anim = 'cast';
          e.animT = 0;
          ev(s, {
            t: 'skill',
            hero: e.id,
            skill: 'simaCocoon',
            x: p.x,
            y: p.y,
          });
        }
      }
      return false;
    }
    case 'marrowWorm': {
      const B = SIMA_BOSS.marrowWorm;
      if (!a && age > 0 && age % B.every === B.every - 1) {
        const h = nearest(s, e.x, e.y);
        const room = s.map.dungeon?.rooms[(s.map.dungeon?.rooms.length ?? 1) - 1];
        if (h && room) {
          // The crack runs from where it dives to where it will come up: past the hero, inside the hall.
          const c = W(room);
          const lim = (room.radius - 1) * 1000;
          const dir = iatan2(h.y - e.y, h.x - e.x);
          let tx = h.x + idiv(cosA(dir) * 800, 10000);
          let ty = h.y + idiv(sinA(dir) * 800, 10000);
          const dd = isqrt(dist2(tx, ty, c.x, c.y));
          if (dd > lim) {
            tx = c.x + idiv((tx - c.x) * lim, dd);
            ty = c.y + idiv((ty - c.y) * lim, dd);
          }
          act(sm, e, 'dive', t, t + B.dive, { tx, ty, r: B.radius });
          quake(s, tx, ty, B.radius, B.dive, dmg(B.dmg), 0);
          e.phase = 7;
          ev(s, { t: 'skill', hero: e.id, skill: 'simaDive', x: e.x, y: e.y });
          return true;
        }
      }
      return a?.kind === 'dive';
    }
  }
  return false;
};

/** A guardian's move comes to its end. */
const end = (s: BattleState, sm: SimaRun, e: Enemy, a: NonNullable<SimaRun['act']>): void => {
  if (a.kind === 'low') {
    // The water comes back with leeches.
    for (let k = 0; k < SIMA_BOSS.leechMother.leeches; k++) {
      const p = hexRing(2)[(k * 3) % 12] as Hex;
      const w = W(p);
      spawnEnemy(s, 'leech', 1, false, 0, e.x + w.x, e.y + w.y);
    }
    ev(s, { t: 'skill', hero: e.id, skill: 'simaRise', x: e.x, y: e.y });
  } else if (a.kind === 'swell') {
    const B = SIMA_BOSS.sporeMother;
    for (const h of s.heroes) {
      if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, B.radius)) continue;
      damageHero(s, h, e.damage, e, 'poison');
      if ((h.frenzy ?? 0) <= 0) {
        h.sleep = imax(h.sleep ?? 0, B.sleepTicks);
        h.moving = false;
      }
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: B.radius, kind: 'simaSpores' });
  } else if (a.kind === 'read') {
    // Read to the end: the pages call nightmares of the stratum.
    const B = SIMA_BOSS.archivist;
    const kinds = (Object.keys(STRATUM[sm.stratum].bestiary) as (NightmareType | 'scribe')[]).filter((k) => k !== 'scribe').sort();
    for (let k = 0; k < B.calls + imax(0, s.heroes.length - 2); k++) {
      const p = W(hexRing(2)[(k * 5) % 12] as Hex);
      spawnEnemy(s, (kinds[k % kinds.length] ?? 'skeleton') as EnemyKind, 1, false, 0, e.x + p.x, e.y + p.y);
    }
    ev(s, { t: 'skill', hero: e.id, skill: 'simaPages', x: e.x, y: e.y });
  } else if (a.kind === 'inhale') {
    // Then it blows: fire in a cone toward where the nearest hero was; an anvil between shelters.
    const B = SIMA_BOSS.bellows;
    sm.act = null;
    act(sm, e, 'blow', s.tick, s.tick + 12, { tx: a.tx, ty: a.ty, r: B.range });
    cone(s, e, B.range, B.arc, mulBp(mulBp(mulBp(B.dmg, sm.dmgBp), simaDmgBp(sm.floor)), SIMA.guardianDmgBp), 'fire', a.tx, a.ty, true);
    return;
  } else if (a.kind === 'dive') {
    // It comes up at the end of its crack, and lies dazed a moment.
    e.x = a.tx;
    e.y = a.ty;
    e.phase = 0;
    e.stun = imax(e.stun, SIMA_BOSS.marrowWorm.dazed);
    ev(s, { t: 'skill', hero: e.id, skill: 'simaSurface', x: e.x, y: e.y });
  }
};

/** Whether a point is in a cone of `arc` each side of `dir` from `e`, out to `range`; with `shelter`, not behind an anvil. */
export const inSimaCone = (s: BattleState, e: { x: number; y: number }, range: number, arc: number, dir: number, shelter: boolean, x: number, y: number): boolean => {
  if (!withinRange(e.x, e.y, x, y, range)) return false;
  const dd = ((((iatan2(y - e.y, x - e.x) - dir) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS) - ANGLE_STEPS / 2;
  if (dd > arc || dd < -arc) return false;
  const sm = s.dungeon?.sima;
  return !(shelter && sm?.props.some((p) => p.kind === 'anvil' && onSegment(p.x, p.y, e.x, e.y, x, y, p.r2) && dist2(e.x, e.y, p.x, p.y) < dist2(e.x, e.y, x, y)));
};

/** A cone of breath or fire from `e` toward a hero (or a point): heroes inside take `dmg`; with `shelter`, an anvil in between shields. */
const cone = (s: BattleState, e: Enemy, range: number, arc: number, dmg: number, type: 'frost' | 'fire', ax?: number, ay?: number, shelter = false): void => {
  const aim = ax !== undefined && ay !== undefined ? { x: ax, y: ay } : nearest(s, e.x, e.y);
  if (!aim) return;
  const dir = iatan2(aim.y - e.y, aim.x - e.x);
  e.facing = dir;
  e.anim = 'cast';
  e.animT = 0;
  ev(s, {
    t: 'shot',
    from: e.id,
    to: 0,
    kind: type === 'frost' ? 'frostBreath' : 'simaBlow',
    x: e.x,
    y: e.y,
    tx: aim.x,
    ty: aim.y,
  });
  for (const h of s.heroes) {
    if (!standing(h) || !inSimaCone(s, e, range, arc, dir, shelter, h.x, h.y)) continue;
    damageHero(s, h, dmg, e, type);
    if (type === 'frost') chill(s, h, 2);
  }
};

/** What a blow does to a guardian under its rule: the mother covered by water, the prism behind its crystals. */
export const simaBossTakes = (s: BattleState, e: Enemy, dmg: number): number => {
  const sm = s.dungeon?.sima;
  if (!sm) return dmg;
  if (e.kind === 'leechMother' && !(sm.act?.kind === 'low' && sm.act.by === e.id)) return mulBp(dmg, SIMA_BOSS.leechMother.takenInWaterBp);
  if (e.kind === 'prism' && s.enemies.some((o) => o.kind === 'crystal' && o.hp > 0 && o.owner === e.id)) return mulBp(dmg, SIMA_BOSS.prism.shieldedBp);
  if (e.kind === 'marrowWorm' && e.phase === 7) return 0;
  return dmg;
};

/** After a blow lands on a guardian: the archivist's reading gives way under it. */
export const simaBossHit = (s: BattleState, e: Enemy, dmg: number): void => {
  const a = s.dungeon?.sima?.act;
  if (!a || a.by !== e.id || a.kind !== 'read' || a.bar === undefined) return;
  a.bar = imax(0, a.bar - dmg);
  if (a.bar > 0) return;
  // Cut short: it staggers and the pages fall.
  const sm = s.dungeon?.sima as SimaRun;
  sm.act = null;
  e.stun = imax(e.stun, 24);
  ev(s, { t: 'skill', hero: e.id, skill: 'simaReadCut', x: e.x, y: e.y });
};

/** Each tick, the guardian's things on the floor: ice that holds a hero, cocoons about to hatch. */
export const simaPropsTick = (s: BattleState): void => {
  for (const o of s.enemies) {
    if (o.hp <= 0) continue;
    if (o.kind === 'iceBlock') {
      const h = o.encases ? findHero(s, o.encases) : null;
      o.timer--;
      if (!h || h.downTicks > 0 || o.timer <= 0) {
        o.hp = 0;
        if (h) h.stun = 0;
        ev(s, { t: 'boom', x: o.x, y: o.y, r: 500, kind: 'simaIceBreak' });
        continue;
      }
      h.stun = imax(h.stun, 2);
      o.x = h.x;
      o.y = h.y;
    } else if (o.kind === 'cocoon') {
      if (--o.timer > 0) continue;
      o.hp = 0;
      const B = SIMA_BOSS.broodQueen;
      for (let k = 0; k < B.brood + imax(0, s.heroes.length - 3); k++) {
        const w = W(hexRing(1)[k % 6] as Hex);
        spawnEnemy(s, 'spiderling', 1, false, 0, o.x + idiv(w.x * 4, 10), o.y + idiv(w.y * 4, 10));
      }
      ev(s, { t: 'boom', x: o.x, y: o.y, r: 700, kind: 'brood' });
    }
  }
};

/** An ice block broken by blows sets its hero free at once. */
export const simaPropDied = (s: BattleState, e: Enemy): void => {
  if (e.kind !== 'iceBlock' || !e.encases) return;
  const h = findHero(s, e.encases);
  if (h) h.stun = 0;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'simaIceBreak' });
};

/** Keeps the noise of `NIGHTMARES` honest: the guardians are big. */
export const simaBossBig = (k: EnemyKind): boolean => SIMA_GUARDIAN_KINDS.has(k) || !!NIGHTMARES[k].big;
